/**
 * Backend enum mirrors — values MUST match the C# enums byte-for-byte.
 *
 * Source: Saas.FieldTrix.Shared/CommonEnums.cs and
 *         Saas.FieldTrix.ObjectModel/DispatchBoard/*.cs
 *
 * Any change to the C# enum (add/remove/renumber) requires a corresponding
 * update here. Numeric values are deliberate — the backend serialises
 * enums as ints by default and we send/receive ints over the wire.
 */

/* ── Dispatch Board module ─────────────────────────────────────────────── */

/** DispatchActivityType (`DispatchBoard/DispatchActivityType.cs`) */
export enum DispatchActivityType {
  Job = 1,
  Training = 2,
  Meeting = 3,
  Other = 4,
}

/** DispatchActionIntent (`DispatchBoard/DispatchActionIntent.cs`) */
export enum DispatchActionIntent {
  None = 0,
  Erect = 1,
  Modify = 2,
  Dismantle = 3,
  Mixed = 4,
  Inspection = 5,
  Maintenance = 6,
}

/** DispatchActivityStatus (`DispatchBoard/DispatchActivityStatus.cs`) */
export enum DispatchActivityStatus {
  Scheduled = 1,
  Completed = 4,
  Cancelled = 6,
}

/** DispatchTaskStatus (`DispatchBoard/DispatchTaskStatus.cs`) */
export enum DispatchTaskStatus {
  Pending = 0,
  Completed = 1,
  Partial = 2,
  NotDone = 3,
  /** Removed at capture — not required; no labour, no roll-over (see backend). */
  Cancelled = 4,
}

/* ── CommonEnums (touched by capture pipeline) ─────────────────────────── */

/**
 * CommonEnums.ScaffoldTaskType — used on DispatchActivityTask.
 * VERIFIED against Saas.FieldTrix.Shared/CommonEnums.cs:
 * Erect=1, Modify=2, Dismantle=3, Inspection=4, Maintenance=5, General=6.
 * No Handover here — handover is part of scaffold lifecycle, not a task.
 */
export enum ScaffoldTaskType {
  Erect = 1,
  Modify = 2,
  Dismantle = 3,
  Inspection = 4,
  Maintenance = 5,
  General = 6,
}

/**
 * CommonEnums.WorkItemType — set on WorkOrderTask, surfaced on unplanned-tasks rail.
 * VERIFIED: Labour=1, Structure=2, Logistics=3, Custom=4. No 99/Other.
 */
export enum WorkItemType {
  Labour = 1,
  Structure = 2,
  Logistics = 3,
  Custom = 4,
}

/**
 * CommonEnums.ChargeUpType — shown on activity card as QT/VQ/CU chip.
 * VERIFIED: only THREE values — Quoted=1, VariationQuoted=2, ChargeUp=3.
 * No Internal — the activity card's "INT" chip should be removed; backend
 * has no concept of Internal charge type.
 */
export enum ChargeUpType {
  Quoted = 1,
  VariationQuoted = 2,
  ChargeUp = 3,
}

/** CommonEnums.CartageVehicleType — billing tier for vehicle assets. */
export enum CartageVehicleType {
  Ute = 1,
  UteAndTrailer = 2,
  SmallTruck = 3,
  LargeTruck = 4,
  Trailer = 5,
  Other = 99,
}

/** CommonEnums.CartageType — distance bucket for cartage rate cascade. */
export enum CartageType {
  Local = 1,        // < 50km
  Regional = 2,     // 50-200km
  LongDistance = 3, // > 200km
  Delivery = 4,
}

/** CommonEnums.TimesheetActivityType — labour line classification on ScaffoldTaskLabour. */
export enum TimesheetActivityType {
  Work = 1,
  Travel = 2,
  Standby = 3,
  Loading = 4,
  YardWork = 5,
  Other = 99,
}

/** CommonEnums.AssetClass — surfaces in UI to show Tool/Vehicle/Equipment chips. */
export enum AssetClass {
  Vehicle = 1,
  Tool = 2,
  Equipment = 3,
}
